/**
 * authService.js — JWT email/password + Emergent Google OAuth.
 * Maps 1:1 to app/services/auth.py on the backend.
 *
 * All endpoints here surface their errors INLINE on the auth-flow page
 * (login form, verify page, etc.), so the global axios interceptor's
 * toast would just double up. We pass `suppressErrorToast: true` to
 * opt out of the toast — the rejected promise still propagates.
 */
import { apiClient } from "./api";

const SILENT = { suppressErrorToast: true };

export const authService = {
  me: () => apiClient.get("/auth/me", SILENT).then((r) => r.data),
  register: (email, password, name) =>
    apiClient.post("/auth/register", { email, password, name }, SILENT).then((r) => r.data),
  login: (email, password) =>
    apiClient.post("/auth/login", { email, password }, SILENT).then((r) => r.data),
  logout: () => apiClient.post("/auth/logout", null, SILENT).then((r) => r.data),
  googleSession: (sessionId) =>
    apiClient.get(`/auth/session?session_id=${sessionId}`, SILENT).then((r) => r.data),
  googleAuth: (credential) =>
    apiClient.post("/auth/google", { credential }, SILENT).then((r) => r.data),

  // Email verification + password reset (KNOWN_ISSUES §1.22).
  verifyEmail: (token) =>
    apiClient
      .get(`/auth/verify?token=${encodeURIComponent(token)}`, SILENT)
      .then((r) => r.data),
  resendVerification: (email) =>
    apiClient.post("/auth/resend-verification", { email }, SILENT).then((r) => r.data),
  forgotPassword: (email) =>
    apiClient.post("/auth/forgot-password", { email }, SILENT).then((r) => r.data),
  resetPassword: (token, newPassword) =>
    apiClient
      .post("/auth/reset-password", { token, new_password: newPassword }, SILENT)
      .then((r) => r.data),

  // GDPR — account delete is shown inline (modal); export uses the global
  // toast so a 401 / 500 surfaces without extra plumbing on the caller.
  deleteAccount: (confirm, password) =>
    apiClient
      .delete("/auth/me", { data: { confirm, password: password || "" }, suppressErrorToast: true })
      .then((r) => r.data),
  exportData: () => apiClient.get("/auth/me/export").then((r) => r.data),

  // Wipe every active session for the user (this device + every other
  // device they're signed in on). Returns { ok, sessions_deleted }.
  logoutEverywhere: () =>
    apiClient.post("/auth/logout-all", null, SILENT).then((r) => r.data),

  // 2FA (KNOWN_ISSUES §1.49). All endpoints suppress the global error
  // toast — every page that calls them shows errors inline.
  twoFAEnrollStart: () =>
    apiClient.post("/auth/2fa/enroll/start", null, SILENT).then((r) => r.data),
  twoFAEnrollVerify: (code) =>
    apiClient.post("/auth/2fa/enroll/verify", { code }, SILENT).then((r) => r.data),
  twoFADisable: (password) =>
    apiClient.post("/auth/2fa/disable", { password }, SILENT).then((r) => r.data),
  twoFARegenerateCodes: (password) =>
    apiClient.post("/auth/2fa/regenerate-codes", { password }, SILENT).then((r) => r.data),
  twoFAVerify: (challengeToken, { code, recoveryCode } = {}) =>
    apiClient
      .post(
        "/auth/2fa/verify",
        {
          challenge_token: challengeToken,
          code: code || null,
          recovery_code: recoveryCode || null,
        },
        SILENT,
      )
      .then((r) => r.data),
};
